import { Meal, MealIngredientItem } from './types';
import { localStore } from './storage';
import { describeMealDirectOrProxy } from './gemini';
import { matchIngredientToFodmap, getRealisticCulinaryServing } from './fodmapEngine';

// Ingredient Canonicalization and Cleaning Utilities

export const DESCRIPTOR_STOPWORDS = new Set([
  'whole', 'raw', 'white', 'cooked', 'fresh', 'warm', 'hot', 'cold', 'plain',
  'fried', 'baked', 'roasted', 'steamed', 'grilled', 'diced', 'sliced', 'minced',
  'chopped', 'organic', 'pure', 'extra', 'unseasoned', 'seasoned', 'boiled',
  'common', 'firm', 'ripe', 'unripe', 'standard', 'generic', 'conventional', 'commercial',
  'continental', 'freshly squeezed', 'squeezed', 'sweetened', 'unsweetened',
  'evaporated', 'condensed', 'skim', 'regular fat', 'low fat', 'full fat', 'fat free',
  'reduced fat', 'all cuts', 'all cut', 'cuts', 'cut', 'peeled', 'unpeeled',
  'trimmed', 'untrimmed', 'skinless', 'boneless', 'bone in', 'bone-in', 'lean',
  'extra lean', 'shredded', 'pureed', 'cubed', 'mashed', 'crushed', 'grated', 'mixed',
  'homemade', 'prepared', 'serving', 'servings', 'portion', 'portions', 'plate',
  'bowl', 'dish', 'cup', 'glass', 'piece', 'pieces', 'slice', 'slices', 'tbsp',
  'tsp', 'grams', 'gram', 'large', 'small', 'medium', 'heavy', 'light', 'tasty',
  'delicious', 'rich', 'quick', 'snack', 'breakfast', 'lunch', 'dinner', 'food',
  'meal', 'eating', 'ate', 'felt', 'hungry', 'full', 'late', 'vegetables', 'vegetable',
  'juice', 'cow'
]);

const CANONICAL_MAP: Record<string, string> = {
  // Common Standalone Descriptor Stopwords (mapped to empty string so they are eliminated)
  'common': '',
  'firm': '',
  'ripe': '',
  'unripe': '',
  'standard': '',
  'generic': '',
  'conventional': '',
  'commercial': '',
  'continental': '',
  'freshly squeezed': '',
  'squeezed': '',
  'sweetened': '',
  'unsweetened': '',
  'evaporated': '',
  'condensed': '',
  'skim': '',
  'cow': '',
  'vegetables': '',
  'vegetable': '',
  'juice': '',
  'all cuts': '',
  'all cut': '',
  'cuts': '',
  'cut': '',
  'peeled': '',
  'unpeeled': '',
  'regular fat': '',
  'low fat': '',
  'full fat': '',
  'fat free': '',
  'reduced fat': '',
  'trimmed': '',
  'untrimmed': '',
  'skinless': '',
  'boneless': '',
  'bone in': '',
  'bone-in': '',
  'lean': '',
  'extra lean': '',
  'shredded': '',
  'cubed': '',
  'grated': '',
  'white': '',
  'raw': '',
  'whole': '',
  'cooked': '',
  'fresh': '',
  'warm': '',
  'hot': '',
  'cold': '',
  'plain': '',
  'organic': '',
  'diced': '',
  'sliced': '',
  'chopped': '',
  'minced': '',
  'boiled': '',
  'fried': '',
  'baked': '',
  'roasted': '',
  'steamed': '',
  'grilled': '',
  'unseasoned': '',

  // Meats
  'chicken breast': 'Chicken',
  'chicken thighs': 'Chicken',
  'chicken thigh': 'Chicken',
  'chicken wings': 'Chicken',
  'chicken wing': 'Chicken',
  'chicken fillet': 'Chicken',
  'roast chicken': 'Chicken',
  'grilled chicken': 'Chicken',
  'chicken meat': 'Chicken',
  'beef mince': 'Beef',
  'ground beef': 'Beef',
  'beef steak': 'Beef',
  'minced beef': 'Beef',
  'steak': 'Beef',
  'beef burger': 'Beef',
  'pork chop': 'Pork',
  'pork loin': 'Pork',
  'pork mince': 'Pork',
  'ground pork': 'Pork',
  'bacon': 'Pork',
  'ham': 'Pork',
  'gammon': 'Pork',
  'pork belly': 'Pork',
  'turkey breast': 'Turkey',
  'ground turkey': 'Turkey',
  'lamb chop': 'Lamb',
  'ground lamb': 'Lamb',

  // Seafood
  'salmon fillet': 'Salmon',
  'smoked salmon': 'Salmon',
  'tuna steak': 'Tuna',
  'canned tuna': 'Tuna',
  'cod fillet': 'Cod',
  'king prawns': 'Prawns',
  'shrimp': 'Prawns',
  'shrimps': 'Prawns',

  // Eggs & Egg Dishes
  'egg': 'Egg',
  'eggs': 'Egg',
  'whole egg': 'Egg',
  'whole eggs': 'Egg',
  'egg white': 'Egg White',
  'egg whites': 'Egg White',
  'egg yolk': 'Egg Yolk',
  'egg yolks': 'Egg Yolk',
  'fried egg': 'Egg',
  'fried eggs': 'Egg',
  'scrambled egg': 'Egg',
  'scrambled eggs': 'Egg',
  'poached egg': 'Egg',
  'poached eggs': 'Egg',
  'boiled egg': 'Egg',
  'boiled eggs': 'Egg',
  'hard boiled egg': 'Egg',
  'hard boiled eggs': 'Egg',
  'hard-boiled egg': 'Egg',
  'hard-boiled eggs': 'Egg',
  'omelet': 'Omelette',
  'omelette': 'Omelette',

  // Potatoes & Root Vegetables
  'potato': 'Potato',
  'potatoes': 'Potato',
  'raw potato': 'Potato',
  'raw potatoes': 'Potato',
  'fresh potato': 'Potato',
  'fresh potatoes': 'Potato',
  'baked potato': 'Baked Potato',
  'baked potatoes': 'Baked Potato',
  'jacket potato': 'Baked Potato',
  'jacket potatoes': 'Baked Potato',
  'boiled potato': 'Boiled Potatoes',
  'boiled potatoes': 'Boiled Potatoes',
  'steamed potato': 'Boiled Potatoes',
  'steamed potatoes': 'Boiled Potatoes',
  'roast potato': 'Roast Potatoes',
  'roast potatoes': 'Roast Potatoes',
  'roasted potato': 'Roast Potatoes',
  'roasted potatoes': 'Roast Potatoes',
  'mashed potato': 'Mashed Potatoes',
  'mashed potatoes': 'Mashed Potatoes',
  'mash': 'Mashed Potatoes',
  'yukon gold': 'Yukon Gold Potato',
  'yukon gold potato': 'Yukon Gold Potato',
  'yukon gold potatoes': 'Yukon Gold Potato',
  'yellow potato': 'Yukon Gold Potato',
  'yellow potatoes': 'Yukon Gold Potato',
  'baby potato': 'Baby Potatoes',
  'baby potatoes': 'Baby Potatoes',
  'new potato': 'Baby Potatoes',
  'new potatoes': 'Baby Potatoes',
  'charlotte potato': 'Baby Potatoes',
  'charlotte potatoes': 'Baby Potatoes',
  'jersey royals': 'Baby Potatoes',
  'jersey royal potatoes': 'Baby Potatoes',
  'fingerling potato': 'Fingerling Potatoes',
  'fingerling potatoes': 'Fingerling Potatoes',
  'kipfler potato': 'Fingerling Potatoes',
  'kipfler potatoes': 'Fingerling Potatoes',
  'purple potato': 'Purple Potato',
  'purple potatoes': 'Purple Potato',
  'blue potato': 'Purple Potato',
  'blue potatoes': 'Purple Potato',
  'maris piper': 'Maris Piper Potato',
  'maris piper potato': 'Maris Piper Potato',
  'maris piper potatoes': 'Maris Piper Potato',
  'king edward': 'King Edward Potato',
  'king edward potato': 'King Edward Potato',
  'king edward potatoes': 'King Edward Potato',
  'russet potato': 'Russet Potato',
  'russet potatoes': 'Russet Potato',
  'idaho potato': 'Russet Potato',
  'idaho potatoes': 'Russet Potato',
  'red potato': 'Red Potato',
  'red potatoes': 'Red Potato',
  'white potato': 'White Potato',
  'white potatoes': 'White Potato',
  'french fries': 'French Fries',
  'chips': 'French Fries',
  'hot chips': 'French Fries',
  'fries': 'French Fries',
  'potato wedges': 'Potato Wedges',
  'wedges': 'Potato Wedges',
  'hash brown': 'Hash Browns',
  'hash browns': 'Hash Browns',
  'potato rosti': 'Hash Browns',
  'rosti': 'Hash Browns',
  'potato salad': 'Potato Salad',
  'sweet potato': 'Sweet Potato',
  'sweet potatoes': 'Sweet Potato',
  'kumara': 'Sweet Potato',
  'baked sweet potato': 'Baked Sweet Potato',
  'roasted sweet potato': 'Baked Sweet Potato',
  'purple sweet potato': 'Purple Sweet Potato',
  'japanese sweet potato': 'Purple Sweet Potato',
  'sweet potato fries': 'Sweet Potato Fries',
  'sweet potato wedges': 'Sweet Potato Fries',

  // Alliums & Aromatics (High FODMAP & Low FODMAP Safe Alternatives)
  'garlic-infused olive oil': 'Garlic-Infused Olive Oil',
  'garlic-infused oil': 'Garlic-Infused Olive Oil',
  'garlic infused olive oil': 'Garlic-Infused Olive Oil',
  'garlic infused oil': 'Garlic-Infused Olive Oil',
  'spring onion green tops': 'Spring Onion Green Tops',
  'spring onion greens': 'Spring Onion Green Tops',
  'spring onion green tops (scallion greens)': 'Spring Onion Green Tops',
  'scallion greens': 'Spring Onion Green Tops',
  'scallion green tops': 'Spring Onion Green Tops',
  'green spring onion': 'Spring Onion Green Tops',
  'spring onion heads': 'Spring Onion Heads',
  'spring onion head': 'Spring Onion Heads',
  'spring onion bulb': 'Spring Onion Heads',
  'spring onion bulbs': 'Spring Onion Heads',
  'spring onion whites': 'Spring Onion Heads',
  'scallion heads': 'Spring Onion Heads',
  'scallion head': 'Spring Onion Heads',
  'scallion bulb': 'Spring Onion Heads',
  'scallion bulbs': 'Spring Onion Heads',
  'scallion whites': 'Spring Onion Heads',
  'fresh chives': 'Chives',
  'chives': 'Chives',
  'chive': 'Chives',
  'leek green leaves': 'Leek Green Leaves',
  'leek leaves': 'Leek Green Leaves',
  'garlic powder': 'Garlic',
  'minced garlic': 'Garlic',
  'crushed garlic': 'Garlic',
  'garlic cloves': 'Garlic',
  'garlic clove': 'Garlic',
  'garlic extract': 'Garlic',
  'garlic puree': 'Garlic',
  'onion powder': 'Onion',
  'diced onion': 'Onion',
  'red onion': 'Onion',
  'white onion': 'Onion',
  'yellow onion': 'Onion',
  'shallots': 'Onion',
  'shallot': 'Onion',
  'spring onion': 'Spring Onion',
  'green onion': 'Spring Onion',
  'scallions': 'Spring Onion',
  'scallion': 'Spring Onion',

  // Mushrooms
  'oyster mushrooms': 'Oyster Mushrooms',
  'oyster mushroom': 'Oyster Mushrooms',
  'canned champignons': 'Canned Champignons',
  'button mushrooms': 'Mushrooms',
  'cremini mushrooms': 'Mushrooms',
  'portobello mushrooms': 'Mushrooms',

  // Fats & Oils
  'extra virgin olive oil': 'Olive Oil',
  'virgin olive oil': 'Olive Oil',
  'olive oil': 'Olive Oil',
  'rapeseed oil': 'Vegetable Oil',
  'canola oil': 'Vegetable Oil',
  'sunflower oil': 'Vegetable Oil',
  'sunflower seed oil': 'Vegetable Oil',
  'vegetable oil': 'Vegetable Oil',
  'coconut oil': 'Coconut Oil',
  'palm oil': 'Palm Oil',
  'sesame oil': 'Sesame Oil',
  'unsalted butter': 'Butter',
  'salted butter': 'Butter',

  // Dairy & Alternatives
  'whole milk': 'Milk',
  'semi-skimmed milk': 'Milk',
  'semi skimmed milk': 'Milk',
  'skimmed milk': 'Milk',
  'low fat milk': 'Milk',
  'fat free milk': 'Milk',
  'reduced fat milk': 'Milk',
  'cows milk': 'Milk',
  'cow milk': 'Milk',
  'pasteurised milk': 'Milk',
  'pasteurized milk': 'Milk',
  'homogenised milk': 'Milk',
  'homogenized milk': 'Milk',
  'fresh milk': 'Milk',
  'cheddar cheese': 'Cheddar Cheese',
  'cheddar': 'Cheddar Cheese',
  'parmesan cheese': 'Parmesan Cheese',
  'parmesan': 'Parmesan Cheese',
  'mozzarella cheese': 'Mozzarella Cheese',
  'mozzarella': 'Mozzarella Cheese',
  'feta cheese': 'Feta Cheese',
  'feta': 'Feta Cheese',
  'cream cheese': 'Cream Cheese',
  'cottage cheese': 'Cottage Cheese',
  'ricotta cheese': 'Ricotta Cheese',
  'ricotta': 'Ricotta Cheese',
  'greek yogurt': 'Greek Yogurt',
  'greek yoghurt': 'Greek Yogurt',
  'natural yogurt': 'Yogurt',
  'natural yoghurt': 'Yogurt',
  'plain yogurt': 'Yogurt',
  'plain yoghurt': 'Yogurt',
  'oat milk': 'Oat Milk',
  'almond milk': 'Almond Milk',
  'soy milk': 'Soy Milk',
  'soya milk': 'Soy Milk',
  'coconut milk': 'Coconut Milk',
  'rice milk': 'Rice Milk',
  'cashew milk': 'Cashew Milk',

  // Grains & Starches
  'maize flour': 'Maize Flour',
  'corn flour': 'Corn Flour',
  'cornmeal': 'Cornmeal',
  'polenta': 'Polenta',
  'cornstarch': 'Cornstarch',
  'corn starch': 'Cornstarch',
  'rice flour': 'Rice Flour',
  'white rice flour': 'Rice Flour',
  'brown rice flour': 'Rice Flour',
  'tapioca flour': 'Tapioca Flour',
  'tapioca starch': 'Tapioca Flour',
  'cassava flour': 'Cassava Flour',
  'arrowroot flour': 'Arrowroot Flour',
  'almond flour': 'Almond Flour',
  'almond meal': 'Almond Flour',
  'coconut flour': 'Coconut Flour',
  'buckwheat flour': 'Buckwheat Flour',
  'oat flour': 'Oat Flour',
  'sorghum flour': 'Sorghum Flour',
  'millet flour': 'Millet Flour',
  'gluten free flour': 'Gluten-Free Flour',
  'gluten-free flour': 'Gluten-Free Flour',
  'wheat flour': 'Wheat Flour',
  'white wheat flour': 'Wheat Flour',
  'plain flour': 'Wheat Flour',
  'all purpose flour': 'Wheat Flour',
  'sourdough bread': 'Sourdough Bread',
  'sourdough': 'Sourdough Bread',
  'white bread': 'White Bread',
  'wholemeal bread': 'Wholemeal Bread',
  'wholegrain bread': 'Wholegrain Bread',
  'brown rice': 'Brown Rice',
  'white rice': 'White Rice',
  'jasmine rice': 'Jasmine Rice',
  'basmati rice': 'Basmati Rice',
  'penne pasta': 'Pasta',
  'spaghetti pasta': 'Pasta',
  'rolled oats': 'Rolled Oats',
  'porridge oats': 'Rolled Oats',
  'oat flakes': 'Oats',

  // Sugars
  'cane sugar': 'Cane Sugar',
  'white sugar': 'White Sugar',
  'brown sugar': 'Brown Sugar',
  'granulated sugar': 'Granulated Sugar',
  'caster sugar': 'Caster Sugar',
  'icing sugar': 'Icing Sugar',
  'glucose syrup': 'Corn Syrup',
  'high fructose corn syrup': 'Corn Syrup',
  'maple syrup': 'Maple Syrup',
  'honey': 'Honey',
};

const SORTED_CANONICAL_ENTRIES = Object.entries(CANONICAL_MAP).sort((a, b) => b[0].length - a[0].length);

export interface CompositeDishInfo {
  matchedKey: string;
  category: 'Breakfast' | 'Lunch' | 'Dinner' | 'Snack' | 'Drink';
  ingredients: string[];
}

export const COMPOSITE_DISH_RECIPES: Record<string, { category: 'Breakfast' | 'Lunch' | 'Dinner' | 'Snack' | 'Drink'; ingredients: string[] }> = {
  // Breakfast & Bakery
  'pancake': { category: 'Breakfast', ingredients: ['Wheat Flour', 'Eggs', 'Milk', 'Butter', 'Sugar', 'Baking Powder'] },
  'pancakes': { category: 'Breakfast', ingredients: ['Wheat Flour', 'Eggs', 'Milk', 'Butter', 'Sugar', 'Baking Powder'] },
  'waffle': { category: 'Breakfast', ingredients: ['Wheat Flour', 'Eggs', 'Milk', 'Butter', 'Sugar', 'Baking Powder'] },
  'waffles': { category: 'Breakfast', ingredients: ['Wheat Flour', 'Eggs', 'Milk', 'Butter', 'Sugar', 'Baking Powder'] },
  'crepe': { category: 'Breakfast', ingredients: ['Wheat Flour', 'Eggs', 'Milk', 'Butter', 'Sugar'] },
  'crepes': { category: 'Breakfast', ingredients: ['Wheat Flour', 'Eggs', 'Milk', 'Butter', 'Sugar'] },
  'french toast': { category: 'Breakfast', ingredients: ['Bread', 'Eggs', 'Milk', 'Butter', 'Cinnamon', 'Sugar'] },
  'porridge': { category: 'Breakfast', ingredients: ['Oats', 'Milk', 'Honey'] },
  'oatmeal': { category: 'Breakfast', ingredients: ['Oats', 'Milk', 'Honey'] },
  'granola': { category: 'Breakfast', ingredients: ['Oats', 'Honey', 'Almonds', 'Sunflower Seeds', 'Coconut Oil'] },
  'croissant': { category: 'Breakfast', ingredients: ['Wheat Flour', 'Butter', 'Milk', 'Sugar', 'Yeast', 'Eggs'] },
  'pain au chocolat': { category: 'Breakfast', ingredients: ['Wheat Flour', 'Butter', 'Milk', 'Dark Chocolate', 'Sugar', 'Yeast'] },
  'muffin': { category: 'Breakfast', ingredients: ['Wheat Flour', 'Eggs', 'Butter', 'Milk', 'Sugar', 'Baking Powder'] },
  'blueberry muffin': { category: 'Breakfast', ingredients: ['Wheat Flour', 'Eggs', 'Butter', 'Milk', 'Blueberries', 'Sugar', 'Baking Powder'] },
  'scone': { category: 'Breakfast', ingredients: ['Wheat Flour', 'Butter', 'Milk', 'Eggs', 'Sugar', 'Baking Powder'] },
  'scones': { category: 'Breakfast', ingredients: ['Wheat Flour', 'Butter', 'Milk', 'Eggs', 'Sugar', 'Baking Powder'] },
  'banana bread': { category: 'Breakfast', ingredients: ['Banana', 'Wheat Flour', 'Eggs', 'Butter', 'Sugar', 'Baking Powder'] },
  'bagel': { category: 'Breakfast', ingredients: ['Wheat Flour', 'Yeast', 'Water', 'Salt', 'Sugar'] },
  'avocado toast': { category: 'Breakfast', ingredients: ['Bread', 'Avocado', 'Olive Oil', 'Lemon Juice', 'Black Pepper', 'Salt'] },
  'eggs benedict': { category: 'Breakfast', ingredients: ['Eggs', 'Bread', 'Ham', 'Butter', 'Lemon Juice', 'Black Pepper'] },
  'eggs florentine': { category: 'Breakfast', ingredients: ['Eggs', 'Bread', 'Spinach', 'Butter', 'Lemon Juice', 'Black Pepper'] },
  'eggs royale': { category: 'Breakfast', ingredients: ['Eggs', 'Bread', 'Salmon', 'Butter', 'Lemon Juice', 'Black Pepper'] },
  'scrambled eggs': { category: 'Breakfast', ingredients: ['Eggs', 'Butter', 'Milk', 'Black Pepper', 'Salt'] },
  'omelette': { category: 'Breakfast', ingredients: ['Eggs', 'Butter', 'Milk', 'Black Pepper', 'Salt'] },
  'omelet': { category: 'Breakfast', ingredients: ['Eggs', 'Butter', 'Milk', 'Black Pepper', 'Salt'] },
  'full english': { category: 'Breakfast', ingredients: ['Eggs', 'Bacon', 'Pork', 'Baked Beans', 'Mushrooms', 'Tomato', 'Bread', 'Butter'] },
  'english breakfast': { category: 'Breakfast', ingredients: ['Eggs', 'Bacon', 'Pork', 'Baked Beans', 'Mushrooms', 'Tomato', 'Bread', 'Butter'] },

  // Italian & Pasta & Pizza
  'pizza': { category: 'Dinner', ingredients: ['Wheat Flour', 'Tomato', 'Mozzarella Cheese', 'Olive Oil', 'Garlic', 'Basil'] },
  'margherita pizza': { category: 'Dinner', ingredients: ['Wheat Flour', 'Tomato', 'Mozzarella Cheese', 'Olive Oil', 'Garlic', 'Basil'] },
  'pepperoni pizza': { category: 'Dinner', ingredients: ['Wheat Flour', 'Tomato', 'Mozzarella Cheese', 'Pork', 'Olive Oil', 'Garlic'] },
  'carbonara': { category: 'Dinner', ingredients: ['Pasta', 'Eggs', 'Pork', 'Parmesan Cheese', 'Black Pepper', 'Olive Oil'] },
  'spaghetti carbonara': { category: 'Dinner', ingredients: ['Pasta', 'Eggs', 'Pork', 'Parmesan Cheese', 'Black Pepper', 'Olive Oil'] },
  'bolognese': { category: 'Dinner', ingredients: ['Pasta', 'Beef', 'Tomato', 'Onion', 'Garlic', 'Olive Oil', 'Carrots', 'Parmesan Cheese'] },
  'spaghetti bolognese': { category: 'Dinner', ingredients: ['Pasta', 'Beef', 'Tomato', 'Onion', 'Garlic', 'Olive Oil', 'Carrots', 'Parmesan Cheese'] },
  'pasta bolognese': { category: 'Dinner', ingredients: ['Pasta', 'Beef', 'Tomato', 'Onion', 'Garlic', 'Olive Oil', 'Carrots', 'Parmesan Cheese'] },
  'lasagna': { category: 'Dinner', ingredients: ['Pasta', 'Beef', 'Tomato', 'Milk', 'Wheat Flour', 'Butter', 'Mozzarella Cheese', 'Parmesan Cheese', 'Onion', 'Garlic'] },
  'lasagne': { category: 'Dinner', ingredients: ['Pasta', 'Beef', 'Tomato', 'Milk', 'Wheat Flour', 'Butter', 'Mozzarella Cheese', 'Parmesan Cheese', 'Onion', 'Garlic'] },
  'arrabbiata': { category: 'Dinner', ingredients: ['Pasta', 'Tomato', 'Garlic', 'Chili', 'Olive Oil', 'Basil'] },
  'penne arrabbiata': { category: 'Dinner', ingredients: ['Pasta', 'Tomato', 'Garlic', 'Chili', 'Olive Oil', 'Basil'] },
  'pesto pasta': { category: 'Dinner', ingredients: ['Pasta', 'Basil', 'Pine Nuts', 'Parmesan Cheese', 'Olive Oil', 'Garlic'] },
  'pasta pesto': { category: 'Dinner', ingredients: ['Pasta', 'Basil', 'Pine Nuts', 'Parmesan Cheese', 'Olive Oil', 'Garlic'] },
  'alfredo': { category: 'Dinner', ingredients: ['Pasta', 'Butter', 'Cream', 'Parmesan Cheese', 'Garlic', 'Black Pepper'] },
  'fettuccine alfredo': { category: 'Dinner', ingredients: ['Pasta', 'Butter', 'Cream', 'Parmesan Cheese', 'Garlic', 'Black Pepper'] },
  'chicken and mushroom risotto': { category: 'Dinner', ingredients: ['Rice', 'Chicken', 'Mushrooms', 'Butter', 'Onion', 'Garlic', 'Parmesan Cheese', 'Olive Oil'] },
  'chicken mushroom risotto': { category: 'Dinner', ingredients: ['Rice', 'Chicken', 'Mushrooms', 'Butter', 'Onion', 'Garlic', 'Parmesan Cheese', 'Olive Oil'] },
  'mushroom risotto': { category: 'Dinner', ingredients: ['Rice', 'Mushrooms', 'Butter', 'Onion', 'Garlic', 'Parmesan Cheese', 'Olive Oil'] },
  'risotto': { category: 'Dinner', ingredients: ['Rice', 'Mushrooms', 'Butter', 'Onion', 'Garlic', 'Parmesan Cheese', 'Olive Oil'] },
  'soup': { category: 'Lunch', ingredients: ['Carrots', 'Celery', 'Potato', 'Onion', 'Garlic', 'Olive Oil'] },
  'vegetable soup': { category: 'Lunch', ingredients: ['Carrots', 'Celery', 'Potato', 'Tomato', 'Onion', 'Garlic', 'Peas'] },
  'chicken soup': { category: 'Lunch', ingredients: ['Chicken', 'Noodles', 'Carrots', 'Celery', 'Onion', 'Garlic'] },
  'chicken noodle soup': { category: 'Lunch', ingredients: ['Chicken', 'Noodles', 'Carrots', 'Celery', 'Onion', 'Garlic'] },
  'butternut squash soup': { category: 'Lunch', ingredients: ['Butternut Squash', 'Onion', 'Garlic', 'Olive Oil', 'Cream'] },
  'lentil soup': { category: 'Lunch', ingredients: ['Lentils', 'Carrots', 'Celery', 'Tomato', 'Onion', 'Garlic', 'Olive Oil'] },
  'chicken stir fry': { category: 'Dinner', ingredients: ['Chicken', 'Rice', 'Broccoli', 'Bell Pepper', 'Soy Sauce', 'Garlic', 'Sesame Oil'] },
  'beef stir fry': { category: 'Dinner', ingredients: ['Beef', 'Noodles', 'Broccoli', 'Bell Pepper', 'Soy Sauce', 'Garlic', 'Sesame Oil'] },
  'stir fry': { category: 'Dinner', ingredients: ['Chicken', 'Noodles', 'Broccoli', 'Bell Pepper', 'Soy Sauce', 'Garlic', 'Sesame Oil'] },
  'gnocchi': { category: 'Dinner', ingredients: ['Potato', 'Wheat Flour', 'Eggs', 'Salt'] },
  'garlic bread': { category: 'Snack', ingredients: ['Bread', 'Butter', 'Garlic', 'Parsley'] },
  'bruschetta': { category: 'Snack', ingredients: ['Bread', 'Tomato', 'Garlic', 'Olive Oil', 'Basil'] },
  'tiramisu': { category: 'Snack', ingredients: ['Mascarpone Cheese', 'Eggs', 'Sugar', 'Coffee', 'Wheat Flour', 'Cocoa Powder'] },
  'millionaire shortbread': { category: 'Snack', ingredients: ['Wheat Flour', 'Butter', 'Sugar', 'Condensed Milk', 'Milk Chocolate'] },
  'millionaires shortbread': { category: 'Snack', ingredients: ['Wheat Flour', 'Butter', 'Sugar', 'Condensed Milk', 'Milk Chocolate'] },
  'caramel shortbread': { category: 'Snack', ingredients: ['Wheat Flour', 'Butter', 'Sugar', 'Condensed Milk', 'Milk Chocolate'] },
  'shortbread': { category: 'Snack', ingredients: ['Wheat Flour', 'Butter', 'Sugar'] },
  'brownie': { category: 'Snack', ingredients: ['Wheat Flour', 'Butter', 'Sugar', 'Eggs', 'Cocoa Powder', 'Milk Chocolate'] },
  'brownies': { category: 'Snack', ingredients: ['Wheat Flour', 'Butter', 'Sugar', 'Eggs', 'Cocoa Powder', 'Milk Chocolate'] },
  'apple crumble': { category: 'Snack', ingredients: ['Apples', 'Wheat Flour', 'Butter', 'Sugar', 'Cinnamon'] },
  'apple pie': { category: 'Snack', ingredients: ['Apples', 'Wheat Flour', 'Butter', 'Sugar', 'Cinnamon'] },
  'cheesecake': { category: 'Snack', ingredients: ['Cream Cheese', 'Wheat Flour', 'Butter', 'Sugar', 'Cream', 'Eggs'] },
  'carrot cake': { category: 'Snack', ingredients: ['Wheat Flour', 'Carrots', 'Sugar', 'Eggs', 'Vegetable Oil', 'Cream Cheese', 'Walnuts'] },

  // Mexican & Latin
  'burrito': { category: 'Lunch', ingredients: ['Wheat Flour', 'Rice', 'Black Beans', 'Chicken', 'Cheese', 'Tomato', 'Onion', 'Garlic', 'Avocado'] },
  'burrito bowl': { category: 'Lunch', ingredients: ['Rice', 'Black Beans', 'Chicken', 'Cheese', 'Tomato', 'Onion', 'Garlic', 'Avocado'] },
  'taco': { category: 'Dinner', ingredients: ['Corn Tortilla', 'Beef', 'Onion', 'Tomato', 'Cilantro', 'Lime Juice', 'Garlic'] },
  'tacos': { category: 'Dinner', ingredients: ['Corn Tortilla', 'Beef', 'Onion', 'Tomato', 'Cilantro', 'Lime Juice', 'Garlic'] },
  'fajita': { category: 'Dinner', ingredients: ['Chicken', 'Bell Pepper', 'Onion', 'Wheat Flour', 'Olive Oil', 'Garlic', 'Lime Juice'] },
  'fajitas': { category: 'Dinner', ingredients: ['Chicken', 'Bell Pepper', 'Onion', 'Wheat Flour', 'Olive Oil', 'Garlic', 'Lime Juice'] },
  'quesadilla': { category: 'Lunch', ingredients: ['Wheat Flour', 'Cheese', 'Chicken', 'Onion', 'Bell Pepper', 'Tomato'] },
  'guacamole': { category: 'Snack', ingredients: ['Avocado', 'Lime Juice', 'Onion', 'Tomato', 'Cilantro', 'Garlic', 'Salt'] },
  'nachos': { category: 'Snack', ingredients: ['Corn Tortilla', 'Cheese', 'Jalapeno', 'Tomato', 'Sour Cream', 'Black Beans'] },
  'chili con carne': { category: 'Dinner', ingredients: ['Beef', 'Kidney Beans', 'Tomato', 'Onion', 'Garlic', 'Chili', 'Cumin'] },
  'chilli con carne': { category: 'Dinner', ingredients: ['Beef', 'Kidney Beans', 'Tomato', 'Onion', 'Garlic', 'Chili', 'Cumin'] },

  // Asian & Curries
  'pad thai': { category: 'Dinner', ingredients: ['Rice Noodles', 'Eggs', 'Tofu', 'Prawns', 'Peanuts', 'Bean Sprouts', 'Garlic', 'Soy Sauce', 'Lime Juice'] },
  'ramen': { category: 'Dinner', ingredients: ['Noodles', 'Pork', 'Eggs', 'Spring Onion', 'Soy Sauce', 'Garlic', 'Ginger', 'Sesame Oil'] },
  'fried rice': { category: 'Lunch', ingredients: ['Rice', 'Eggs', 'Spring Onion', 'Peas', 'Soy Sauce', 'Garlic', 'Sesame Oil'] },
  'egg fried rice': { category: 'Lunch', ingredients: ['Rice', 'Eggs', 'Spring Onion', 'Peas', 'Soy Sauce', 'Garlic', 'Sesame Oil'] },
  'chicken tikka masala': { category: 'Dinner', ingredients: ['Chicken', 'Tomato', 'Cream', 'Yogurt', 'Onion', 'Garlic', 'Ginger', 'Turmeric', 'Rice'] },
  'tikka masala': { category: 'Dinner', ingredients: ['Chicken', 'Tomato', 'Cream', 'Yogurt', 'Onion', 'Garlic', 'Ginger', 'Turmeric', 'Rice'] },
  'butter chicken': { category: 'Dinner', ingredients: ['Chicken', 'Butter', 'Cream', 'Tomato', 'Onion', 'Garlic', 'Ginger', 'Rice'] },
  'katsu curry': { category: 'Dinner', ingredients: ['Chicken', 'Wheat Flour', 'Eggs', 'Onion', 'Carrots', 'Curry Powder', 'Rice'] },
  'chicken katsu': { category: 'Dinner', ingredients: ['Chicken', 'Wheat Flour', 'Eggs', 'Onion', 'Carrots', 'Curry Powder', 'Rice'] },
  'green curry': { category: 'Dinner', ingredients: ['Chicken', 'Coconut Milk', 'Eggplant', 'Garlic', 'Ginger', 'Rice'] },
  'thai green curry': { category: 'Dinner', ingredients: ['Chicken', 'Coconut Milk', 'Eggplant', 'Garlic', 'Ginger', 'Rice'] },
  'pho': { category: 'Lunch', ingredients: ['Rice Noodles', 'Beef', 'Onion', 'Ginger', 'Cilantro', 'Bean Sprouts', 'Spring Onion'] },
  'spring roll': { category: 'Snack', ingredients: ['Wheat Flour', 'Cabbage', 'Carrots', 'Garlic', 'Soy Sauce', 'Vegetable Oil'] },
  'spring rolls': { category: 'Snack', ingredients: ['Wheat Flour', 'Cabbage', 'Carrots', 'Garlic', 'Soy Sauce', 'Vegetable Oil'] },
  'dumplings': { category: 'Dinner', ingredients: ['Wheat Flour', 'Pork', 'Cabbage', 'Spring Onion', 'Garlic', 'Ginger', 'Soy Sauce', 'Sesame Oil'] },
  'gyoza': { category: 'Snack', ingredients: ['Wheat Flour', 'Pork', 'Cabbage', 'Spring Onion', 'Garlic', 'Ginger', 'Soy Sauce', 'Sesame Oil'] },
  'sushi': { category: 'Lunch', ingredients: ['Rice', 'Salmon', 'Avocado', 'Cucumber', 'Soy Sauce'] },
  'chow mein': { category: 'Dinner', ingredients: ['Noodles', 'Cabbage', 'Carrots', 'Spring Onion', 'Soy Sauce', 'Garlic', 'Sesame Oil'] },
  'dal': { category: 'Dinner', ingredients: ['Lentils', 'Onion', 'Garlic', 'Ginger', 'Tomato', 'Turmeric', 'Cumin'] },
  'dhal': { category: 'Dinner', ingredients: ['Lentils', 'Onion', 'Garlic', 'Ginger', 'Tomato', 'Turmeric', 'Cumin'] },
  'biryani': { category: 'Dinner', ingredients: ['Rice', 'Chicken', 'Yogurt', 'Onion', 'Garlic', 'Ginger', 'Ghee'] },

  // Burgers, Sandwiches & Pub Food
  'burger': { category: 'Lunch', ingredients: ['Beef', 'Wheat Flour', 'Cheddar Cheese', 'Onion', 'Tomato', 'Lettuce', 'Mayonnaise'] },
  'cheeseburger': { category: 'Lunch', ingredients: ['Beef', 'Wheat Flour', 'Cheddar Cheese', 'Onion', 'Tomato', 'Lettuce', 'Mayonnaise'] },
  'hamburger': { category: 'Lunch', ingredients: ['Beef', 'Wheat Flour', 'Onion', 'Tomato', 'Lettuce', 'Mayonnaise'] },
  'blt': { category: 'Lunch', ingredients: ['Bread', 'Bacon', 'Lettuce', 'Tomato', 'Mayonnaise', 'Butter'] },
  'club sandwich': { category: 'Lunch', ingredients: ['Bread', 'Chicken', 'Bacon', 'Eggs', 'Tomato', 'Lettuce', 'Mayonnaise'] },
  'sandwich': { category: 'Lunch', ingredients: ['Bread', 'Butter', 'Cheese', 'Ham'] },
  'grilled cheese': { category: 'Lunch', ingredients: ['Bread', 'Butter', 'Cheese'] },
  'toastie': { category: 'Lunch', ingredients: ['Bread', 'Butter', 'Cheese'] },
  'cheese toastie': { category: 'Lunch', ingredients: ['Bread', 'Butter', 'Cheese'] },
  'fish and chips': { category: 'Dinner', ingredients: ['Cod', 'Potato', 'Wheat Flour', 'Vegetable Oil', 'Peas'] },
  'shepherds pie': { category: 'Dinner', ingredients: ['Lamb', 'Potato', 'Milk', 'Butter', 'Onion', 'Carrots', 'Peas', 'Garlic'] },
  'cottage pie': { category: 'Dinner', ingredients: ['Beef', 'Potato', 'Milk', 'Butter', 'Onion', 'Carrots', 'Peas', 'Garlic'] },
  'caesar salad': { category: 'Lunch', ingredients: ['Lettuce', 'Chicken', 'Parmesan Cheese', 'Bread', 'Eggs', 'Olive Oil', 'Garlic', 'Lemon Juice'] },
  'greek salad': { category: 'Lunch', ingredients: ['Tomato', 'Cucumber', 'Feta Cheese', 'Red Onion', 'Olives', 'Olive Oil', 'Oregano'] },

  // Beverages & Soups
  'latte': { category: 'Drink', ingredients: ['Coffee', 'Milk'] },
  'cappuccino': { category: 'Drink', ingredients: ['Coffee', 'Milk'] },
  'flat white': { category: 'Drink', ingredients: ['Coffee', 'Milk'] },
  'macchiato': { category: 'Drink', ingredients: ['Coffee', 'Milk'] },
  'mocha': { category: 'Drink', ingredients: ['Coffee', 'Milk', 'Dark Chocolate', 'Sugar'] },
  'hot chocolate': { category: 'Drink', ingredients: ['Milk', 'Dark Chocolate', 'Sugar'] },
  'chai latte': { category: 'Drink', ingredients: ['Black Tea', 'Milk', 'Cinnamon', 'Cardamom', 'Ginger', 'Sugar'] },
  'matcha latte': { category: 'Drink', ingredients: ['Matcha', 'Milk', 'Honey'] },
  'smoothie': { category: 'Drink', ingredients: ['Banana', 'Berries', 'Yogurt', 'Milk', 'Honey'] },
  'tomato soup': { category: 'Lunch', ingredients: ['Tomato', 'Onion', 'Garlic', 'Cream', 'Olive Oil', 'Basil'] },
  'minestrone': { category: 'Lunch', ingredients: ['Tomato', 'Kidney Beans', 'Pasta', 'Carrots', 'Celery', 'Onion', 'Garlic'] },
};

/**
 * Decomposes composite food terms (e.g., "pancakes", "burrito", "carbonara", "pepperoni pizza")
 * into their constituent culinary recipe ingredients.
 */
export function decomposeCompositeDish(text: string): {
  matchedDish?: string;
  category?: 'Breakfast' | 'Lunch' | 'Dinner' | 'Snack' | 'Drink';
  ingredients: string[];
} | null {
  if (!text) return null;

  const lower = text.toLowerCase().trim();
  const collectedIngredients: string[] = [];
  let detectedCategory: ('Breakfast' | 'Lunch' | 'Dinner' | 'Snack' | 'Drink') | undefined;
  let matchedDishName: string | undefined;

  // 1. Direct exact or regex lookup for composite dishes sorted by key length descending (longest match first)
  const sortedDishKeys = Object.keys(COMPOSITE_DISH_RECIPES).sort((a, b) => b.length - a.length);

  for (const dishKey of sortedDishKeys) {
    const dishRegex = new RegExp(`\\b${dishKey.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}\\b`, 'i');
    if (dishRegex.test(lower)) {
      const recipe = COMPOSITE_DISH_RECIPES[dishKey];
      if (!matchedDishName) {
        matchedDishName = dishKey.split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        detectedCategory = recipe.category;
      }
      for (const ing of recipe.ingredients) {
        if (!collectedIngredients.includes(ing)) {
          collectedIngredients.push(ing);
        }
      }
    }
  }

  // 2. Scan for extra ingredients mentioned alongside the dish (e.g. "pancakes with maple syrup and bacon")
  const additionalWords = [
    'maple syrup', 'bacon', 'honey', 'berries', 'blueberries', 'strawberries',
    'banana', 'nutella', 'chocolate chips', 'avocado', 'cheese', 'cheddar', 'mozzarella',
    'parmesan', 'mushrooms', 'spinach', 'tomato', 'sour cream', 'salsa', 'guacamole',
    'fried egg', 'poached egg', 'chicken', 'beef', 'pork', 'jalapeno', 'chili',
    'garlic', 'onion', 'olive oil', 'butter', 'mayo', 'mayonnaise', 'ketchup', 'mustard'
  ];

  for (const extra of additionalWords) {
    const extraRegex = new RegExp(`\\b${extra.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}\\b`, 'i');
    if (extraRegex.test(lower)) {
      const canonical = canonicalizeIngredient(extra);
      if (canonical && !collectedIngredients.includes(canonical)) {
        collectedIngredients.push(canonical);
      }
    }
  }

  if (collectedIngredients.length > 0) {
    return {
      matchedDish: matchedDishName,
      category: detectedCategory,
      ingredients: cleanIngredientsArray(collectedIngredients),
    };
  }

  return null;
}

const CANONICAL_CACHE = new Map<string, string>();

/**
 * Normalizes raw OpenFoodFacts and user-entered ingredient strings:
 * - Strips language prefixes (e.g. "en:water" -> "water")
 * - Replaces underscores with spaces (e.g. "low_fat_milk_94%" -> "low fat milk")
 * - Strips percentage patterns and numbers (e.g. "94%", "_94%", "(94%)")
 * - Canonicalizes dairy, meats, starches, and oils down to simplified parent names
 */
export function canonicalizeIngredient(raw: string): string {
  if (!raw) return '';
  const cached = CANONICAL_CACHE.get(raw);
  if (cached !== undefined) return cached;

  const result = _doCanonicalizeIngredient(raw);
  if (CANONICAL_CACHE.size > 5000) {
    CANONICAL_CACHE.clear();
  }
  CANONICAL_CACHE.set(raw, result);
  return result;
}

export function isProtectedLowFodmap(name: string): boolean {
  if (!name) return false;
  return /garlic[- ]infused|spring\s*onion\s*(green|greens|tops?)|scallion\s*(green|greens|tops?)|chive|leek\s*(green|leaves|tops)|oyster\s*mushroom|canned\s*champignon|lactose[- ]free|gluten[- ]free|sourdough|firm\s*tofu|asafoetida|buckwheat|tapioca|cassava|sorghum|millet|teff/i.test(
    name
  );
}

function _doCanonicalizeIngredient(raw: string): string {
  if (!raw) return '';

  // 1. Prepare fullText without deleting parenthetical contents
  // Convert brackets, punctuation and symbols to spaces so words like "(green tops only)" are preserved
  let cleaned = raw
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/^[a-z]{2,3}:/gi, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\(?\d+(?:\.\d+)?\s*%\)?/g, '')
    .replace(/[()[\]{}.,;:*•/\\|"'’#~!?]/g, ' ')
    .replace(/\b\d+\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (!cleaned) return '';
  if (DESCRIPTOR_STOPWORDS.has(cleaned)) return '';

  // Direct map check
  if (CANONICAL_MAP[cleaned] !== undefined) {
    return CANONICAL_MAP[cleaned];
  }

  // 0. Explicit Certified Low-FODMAP forms & safe plant parts (MUST PRESERVE)
  if (/garlic[- ]infused|infused.*garlic|garlic.*oil/i.test(cleaned)) {
    return 'Garlic-Infused Olive Oil';
  }
  if (
    /spring.*(green|tops|leaves)|scallion.*(green|tops|leaves)|green.*(spring\s*onion|scallion|tops|leaves)|onion.*spring.*green.*tops/i.test(
      cleaned
    ) &&
    !/white.*bulb|bulb.*only|head.*only|whites?\b/i.test(cleaned)
  ) {
    return 'Spring Onion Green Tops';
  }
  if (
    /spring.*(heads?|bulbs?|whites?)|scallion.*(heads?|bulbs?|whites?)|white.*(bulb|head|part|stem)|onion.*spring.*white.*bulb/i.test(
      cleaned
    )
  ) {
    return 'Spring Onion Heads';
  }
  if (/\b(chive|chives)\b/i.test(cleaned)) {
    return 'Chives';
  }
  if (/leek.*(green|leaves|tops)/i.test(cleaned) && !/bulb|white.*stem/i.test(cleaned)) {
    return 'Leek Green Leaves';
  }
  if (/oyster\s*mushroom/i.test(cleaned)) {
    return 'Oyster Mushrooms';
  }
  if (/canned\s*champignon|canned\s*mushroom/i.test(cleaned)) {
    return 'Canned Champignons (Rinsed)';
  }
  if (/lactose[- ]free\s*milk/i.test(cleaned)) {
    return 'Lactose-Free Milk';
  }
  if (/lactose[- ]free\s*cream/i.test(cleaned)) {
    return 'Lactose-Free Cream';
  }
  if (/lactose[- ]free\s*(cheese|cheddar|parmesan|mozzarella)/i.test(cleaned)) {
    return 'Lactose-Free Cheese';
  }
  if (/lactose[- ]free\s*(yogurt|yoghurt)/i.test(cleaned)) {
    return 'Lactose-Free Yogurt';
  }
  if (/lactose[- ]free/i.test(cleaned)) {
    return 'Lactose-Free Dairy';
  }
  if (/gluten[- ]free\s*(bread|toast|bagel|roll)/i.test(cleaned)) {
    return 'Gluten-Free Bread';
  }
  if (/gluten[- ]free\s*(pasta|spaghetti|penne|fusilli|macaroni)/i.test(cleaned)) {
    return 'Gluten-Free Pasta';
  }
  if (/gluten[- ]free\s*flour/i.test(cleaned)) {
    return 'Gluten-Free Flour';
  }
  if (/gluten[- ]free/i.test(cleaned)) {
    return 'Gluten-Free Grain';
  }
  if (/sourdough/i.test(cleaned)) {
    return 'Sourdough Bread';
  }
  if (/firm\s*tofu|extra\s*firm\s*tofu|tempeh/i.test(cleaned)) {
    return 'Firm Tofu';
  }

  // Plant-based milks
  if (/almond\s*milk/i.test(cleaned)) return 'Almond Milk';
  if (/oat\s*milk/i.test(cleaned)) return 'Oat Milk';
  if (/soy\s*milk|soya\s*milk/i.test(cleaned)) return 'Soy Milk';
  if (/coconut\s*milk/i.test(cleaned)) return 'Coconut Milk';
  if (/rice\s*milk/i.test(cleaned)) return 'Rice Milk';
  if (/cashew\s*milk/i.test(cleaned)) return 'Cashew Milk';

  // Cow milk
  if (
    /\bmilk\b/i.test(cleaned) ||
    /\blait\b/i.test(cleaned) ||
    /\bmilch\b/i.test(cleaned)
  ) {
    return 'Milk';
  }

  // Yogurt canonicalization
  if (/greek\s*(yogurt|yoghurt)/i.test(cleaned)) return 'Greek Yogurt';
  if (/\b(yogurt|yoghurt)\b/i.test(cleaned)) {
    return 'Yogurt';
  }

  // Specific Cheeses (preserve varieties)
  if (/cheddar/i.test(cleaned)) return 'Cheddar Cheese';
  if (/parmesan/i.test(cleaned)) return 'Parmesan Cheese';
  if (/mozzarella/i.test(cleaned)) return 'Mozzarella Cheese';
  if (/feta/i.test(cleaned)) return 'Feta Cheese';
  if (/ricotta/i.test(cleaned)) return 'Ricotta Cheese';
  if (/cottage\s*cheese/i.test(cleaned)) return 'Cottage Cheese';
  if (/cream\s*cheese/i.test(cleaned)) return 'Cream Cheese';
  if (/\b(cheese|fromage|kase|kaese)\b/i.test(cleaned)) {
    return 'Cheese';
  }

  // Butter canonicalization (except nut/seed butters)
  if (/\bbutter\b/i.test(cleaned)) {
    if (/\b(peanut|almond|cashew|sunflower|apple|cocoa|shea)\s*butter\b/i.test(cleaned)) {
      const match = cleaned.match(/\b(peanut|almond|cashew|sunflower|apple|cocoa|shea)\s*butter\b/i);
      if (match) {
        const prefix = match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
        return `${prefix} Butter`;
      }
    }
    return 'Butter';
  }

  // Chicken cuts & preparation
  if (/\bchicken\b/i.test(cleaned)) {
    return 'Chicken';
  }

  // Beef cuts & preparation
  if (/\b(beef|steak|minced beef|ground beef)\b/i.test(cleaned)) {
    return 'Beef';
  }

  // Pork varieties
  if (/\bbacon\b/i.test(cleaned)) return 'Bacon';
  if (/\bham\b/i.test(cleaned)) return 'Ham';
  if (/\bprosciutto\b/i.test(cleaned)) return 'Prosciutto';
  if (/\b(pork|gammon|pork belly)\b/i.test(cleaned)) {
    return 'Pork';
  }

  // Turkey
  if (/\bturkey\b/i.test(cleaned)) {
    return 'Turkey';
  }

  // Lamb
  if (/\blamb\b/i.test(cleaned)) {
    return 'Lamb';
  }

  // Salmon
  if (/\bsalmon\b/i.test(cleaned)) {
    return 'Salmon';
  }

  // Tuna
  if (/\btuna\b/i.test(cleaned)) {
    return 'Tuna';
  }

  // Cod
  if (/\bcod\b/i.test(cleaned)) {
    return 'Cod';
  }

  // Prawns / Shrimp
  if (/\b(prawn|prawns|shrimp|shrimps)\b/i.test(cleaned)) {
    return 'Prawns';
  }

  // Olive oil
  if (/olive\s*oil/i.test(cleaned)) {
    return 'Olive Oil';
  }

  // Vegetable / Seed oils
  if (/(rapeseed|canola|sunflower|vegetable)\s*oil/i.test(cleaned)) {
    return 'Vegetable Oil';
  }

  // Distinct Non-Wheat Flours & Starches (PRESERVE EXACT GRAIN)
  if (/\b(maize\s*flour|corn\s*flour|masa\s*harina)\b/i.test(cleaned)) return 'Maize Flour';
  if (/\b(cornmeal|polenta)\b/i.test(cleaned)) return 'Cornmeal';
  if (/\b(cornstarch|corn\s*starch)\b/i.test(cleaned)) return 'Cornstarch';
  if (/\b(rice\s*flour|white\s*rice\s*flour|brown\s*rice\s*flour|sweet\s*rice\s*flour|glutinous\s*rice\s*flour)\b/i.test(cleaned)) return 'Rice Flour';
  if (/\b(rice\s*starch)\b/i.test(cleaned)) return 'Rice Starch';
  if (/\b(almond\s*flour|almond\s*meal)\b/i.test(cleaned)) return 'Almond Flour';
  if (/\b(coconut\s*flour)\b/i.test(cleaned)) return 'Coconut Flour';
  if (/\b(tapioca\s*flour|tapioca\s*starch)\b/i.test(cleaned)) return 'Tapioca Flour';
  if (/\b(cassava\s*flour)\b/i.test(cleaned)) return 'Cassava Flour';
  if (/\b(arrowroot|arrowroot\s*flour|arrowroot\s*starch)\b/i.test(cleaned)) return 'Arrowroot Flour';
  if (/\b(buckwheat\s*flour)\b/i.test(cleaned)) return 'Buckwheat Flour';
  if (/\b(oat\s*flour)\b/i.test(cleaned)) return 'Oat Flour';
  if (/\b(sorghum\s*flour)\b/i.test(cleaned)) return 'Sorghum Flour';
  if (/\b(millet\s*flour)\b/i.test(cleaned)) return 'Millet Flour';
  if (/\b(gluten[- ]free\s*flour|gf\s*flour)\b/i.test(cleaned)) return 'Gluten-Free Flour';

  // Wheat Flour (only when explicitly wheat or generic plain/all-purpose/self-raising flour with no other grain qualifier)
  if (
    /\b(wheat\s*flour|plain\s*flour|all\s*purpose\s*flour|self\s*raising\s*flour|bread\s*flour|semolina\s*flour|semolina)\b/i.test(cleaned) ||
    cleaned === 'flour'
  ) {
    return 'Wheat Flour';
  }

  // Oats
  if (/rolled\s*oats|porridge\s*oats/i.test(cleaned)) return 'Rolled Oats';
  if (/\b(oat\s*flakes|oats)\b/i.test(cleaned)) {
    return 'Oats';
  }

  // Rice
  if (/brown\s*rice/i.test(cleaned)) return 'Brown Rice';
  if (/jasmine\s*rice/i.test(cleaned)) return 'Jasmine Rice';
  if (/basmati\s*rice/i.test(cleaned)) return 'Basmati Rice';
  if (/\b(white\s*rice|rice)\b/i.test(cleaned)) {
    return 'Rice';
  }

  // Garlic
  if (/\bgarlic\b/i.test(cleaned)) {
    return 'Garlic';
  }

  // Spring Onion vs Onion
  if (/\b(spring\s*onion\s*(heads?|bulbs?|whites?)|scallion\s*(heads?|bulbs?|whites?))\b/i.test(cleaned)) {
    return 'Spring Onion Heads';
  }
  if (/\b(spring\s*onion|green\s*onion|scallion|scallions)\b/i.test(cleaned)) {
    return 'Spring Onion';
  }
  if (/\b(onion|shallot|shallots)\b/i.test(cleaned)) {
    return 'Onion';
  }

  // Sugar
  if (/\b(cane\s*sugar|granulated\s*sugar|white\s*sugar|brown\s*sugar|sugar)\b/i.test(cleaned)) {
    return 'Sugar';
  }

  // Prefix or partial dictionary matches (checked longest key first to avoid sub-match collisions e.g. sweet potato vs potato)
  for (const [key, canonical] of SORTED_CANONICAL_ENTRIES) {
    if (cleaned === key || cleaned.startsWith(key + ' ') || cleaned.endsWith(' ' + key)) {
      return canonical;
    }
  }

  // Strip common generic culinary descriptors
  const stripped = cleaned
    .replace(/\b(organic|fresh|natural|raw|dried|cooked|roasted|grilled|diced|sliced|minced|chopped|concentrated|pure|refined|extra|semi|whole)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  const candidate = stripped.length >= 2 ? stripped : cleaned;
  if (DESCRIPTOR_STOPWORDS.has(candidate.toLowerCase())) return '';

  // Capitalize each word nicely
  const result = candidate
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

  return DESCRIPTOR_STOPWORDS.has(result.toLowerCase()) ? '' : result;
}

/**
 * Clean product packaging text:
 * Strips "May contain: ...", "Produced in a factory...", and allergen advisory notices.
 * Handles parentheticals intelligently (extracts inner additives, removes percentages).
 */
export function cleanProductIngredientsText(text: string): string[] {
  if (!text) return [];

  // Remove everything after "may contain" or "allergen" disclaimers
  const disclaimerRegex = /(may contain|produced in a factory|made on equipment|not suitable for|allergen advice|allergens:)/i;
  const match = text.search(disclaimerRegex);
  let coreText = match !== -1 ? text.slice(0, match) : text;

  // Remove percentage notations e.g. (65%), 12%
  coreText = coreText.replace(/\(\s*\d+(\.\d+)?\s*%\s*\)/g, '').replace(/\b\d+(\.\d+)?\s*%/g, '');

  // Split on top-level delimiters (comma, semicolon, bullet, newline) without breaking nested parens
  const tokens: string[] = [];
  let current = '';
  let parenDepth = 0;

  for (let i = 0; i < coreText.length; i++) {
    const char = coreText[i];
    if (char === '(' || char === '[') {
      parenDepth++;
      current += char;
    } else if (char === ')' || char === ']') {
      parenDepth = Math.max(0, parenDepth - 1);
      current += char;
    } else if ((char === ',' || char === ';' || char === '•' || char === '\n') && parenDepth === 0) {
      if (current.trim()) tokens.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  if (current.trim()) tokens.push(current.trim());

  const processedList: string[] = [];

  for (const token of tokens) {
    let item = token.trim();
    if (!item || item.length <= 1) continue;
    if (/^contains\b/i.test(item)) continue;

    // Check if it's an additive category wrapping specific sub-ingredients: e.g. "Emulsifier (Soy Lecithin)"
    const additiveMatch = item.match(/^(?:emulsifier|stabiliser|stabilizer|thickener|preservative|acidity regulator|antioxidant|raising agent|glazing agent|flavouring|flavoring|colour|color|sweetener|firming agent|gelling agent)\s*\((.+)\)$/i);
    if (additiveMatch && additiveMatch[1]) {
      const innerItems = additiveMatch[1].split(/[,;/]/).map((s) => s.trim()).filter(Boolean);
      for (const inner of innerItems) {
        if (inner.length > 1) processedList.push(inner);
      }
      continue;
    }

    // Strip unneeded outer parens or trailing colon
    item = item.replace(/^[:\s\-•]+/, '').replace(/[:\s\-]+$/, '');
    if (item.length > 1) {
      processedList.push(item);
    }
  }

  return cleanIngredientsArray(processedList);
}

/**
 * Cleanly format an ingredient title preserving exact words, title-casing, and stripping punctuation/percentages
 */
export function formatIngredientTitle(raw: string): string {
  if (!raw) return '';
  const cleaned = raw
    .trim()
    .replace(/^[a-z]{2,3}:/gi, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\(?\d+(?:\.\d+)?\s*%\)?/g, '')
    .replace(/[()[\]{}.,;:*•/\\|"'’#~!?]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!cleaned) return '';
  return cleaned
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export interface NormalizedFoodTitleInfo {
  displayName: string;
  isGlutenFree: boolean;
  isLactoseFree: boolean;
  isDairyFree: boolean;
  constituents: string[];
}

/**
 * Normalizes inverted database food names (e.g. from USDA / Monash) like:
 * - "Cucumber, continental, raw" -> "Cucumber"
 * - "Milk, cow, lactose free, regular fat" -> "Lactose-Free Cow's Milk"
 * - "Milk, cow, regular fat" -> "Cow's Milk"
 * - "Juice, orange, freshly squeezed" -> "Orange Juice"
 * - "Bread, gluten free, sourdough" -> "Gluten-Free Bread"
 * - "Tea, black, strong, cow's milk" -> ["Black Tea", "Cow's Milk"]
 */
export function normalizeFoodDatabaseTitle(raw: string): NormalizedFoodTitleInfo {
  if (!raw) {
    return { displayName: '', isGlutenFree: false, isLactoseFree: false, isDairyFree: false, constituents: [] };
  }

  const rawLower = raw.toLowerCase().trim();
  const isGlutenFree = /gluten[- ]free|wheat[- ]free|\bgf\b/i.test(rawLower);
  const isLactoseFree = /lactose[- ]free/i.test(rawLower);
  const isDairyFree = /dairy[- ]free|plant[- ]based|vegan/i.test(rawLower) || /(oat|almond|soy|soya|coconut|rice|cashew|plant)\s*milk/i.test(rawLower);

  // e.g. "Milk, cow, lactose free, regular fat" -> "Lactose-Free Cow's Milk"
  if (/^milk,\s*cow/i.test(rawLower)) {
    const name = isLactoseFree ? "Lactose-Free Cow's Milk" : "Cow's Milk";
    return { displayName: name, isGlutenFree, isLactoseFree, isDairyFree, constituents: [name] };
  }

  // e.g. "Juice, orange, freshly squeezed" -> "Orange Juice"
  if (/^juice,\s*([a-z\s]+)/i.test(rawLower)) {
    const match = rawLower.match(/^juice,\s*([a-z]+)/i);
    const fruit = match ? match[1].trim() : '';
    if (fruit && !DESCRIPTOR_STOPWORDS.has(fruit)) {
      const name = `${fruit.charAt(0).toUpperCase() + fruit.slice(1)} Juice`;
      return { displayName: name, isGlutenFree, isLactoseFree, isDairyFree, constituents: [name] };
    }
  }

  // e.g. "Tea, black, strong, cow's milk" -> "Black Tea" + "Cow's Milk"
  if (/^tea,\s*([a-z]+).*cow'?s\s*milk/i.test(rawLower)) {
    const match = rawLower.match(/^tea,\s*([a-z]+)/i);
    const teaType = match ? match[1].trim() : 'Black';
    const teaName = `${teaType.charAt(0).toUpperCase() + teaType.slice(1)} Tea`;
    const milkName = isLactoseFree ? "Lactose-Free Cow's Milk" : "Cow's Milk";
    return { displayName: `${teaName} with Cow's Milk`, isGlutenFree, isLactoseFree, isDairyFree, constituents: [teaName, milkName] };
  }

  // General inverted food titles with commas
  const segments = raw
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter(Boolean);

  if (segments.length > 1) {
    const mainFood = segments[0].trim();
    const rest = segments.slice(1).join(' ').toLowerCase();

    const hasGf = isGlutenFree || /gluten[- ]free|wheat[- ]free|\bgf\b/i.test(rest);
    const hasLf = isLactoseFree || /lactose[- ]free/i.test(rest);
    const hasDf = isDairyFree || /dairy[- ]free/i.test(rest);

    // If main food is Bread / Pasta / Flour / Grain and GF
    if (/^(bread|toast|pasta|noodles?|flour|couscous|cereal|wrap|roll|bagel|pizza)/i.test(mainFood) && hasGf) {
      const title = `Gluten-Free ${formatIngredientTitle(mainFood)}`;
      return { displayName: title, isGlutenFree: true, isLactoseFree: hasLf, isDairyFree: hasDf, constituents: [title] };
    }

    // If main food is Yogurt / Milk / Cheese / Cream and LF
    if (/^(yogurt|yoghurt|milk|cheese|cream|dairy)/i.test(mainFood) && hasLf) {
      const title = `Lactose-Free ${formatIngredientTitle(mainFood)}`;
      return { displayName: title, isGlutenFree: hasGf, isLactoseFree: true, isDairyFree: hasDf, constituents: [title] };
    }

    // If segments[1] is just a variety like "continental" or "common" or "raw"
    const cleanedMain = formatIngredientTitle(mainFood);
    if (cleanedMain && !DESCRIPTOR_STOPWORDS.has(cleanedMain.toLowerCase())) {
      return { displayName: cleanedMain, isGlutenFree: hasGf, isLactoseFree: hasLf, isDairyFree: hasDf, constituents: [cleanedMain] };
    }
  }

  const cleaned = formatIngredientTitle(raw);
  return { displayName: cleaned, isGlutenFree, isLactoseFree, isDairyFree, constituents: [cleaned] };
}

/**
 * Clean an array of raw ingredient tags or strings preserving exact scanned ingredient names
 */
export function cleanIngredientsArray(items: string[]): string[] {
  const seen = new Set<string>();
  const cleanList: string[] = [];

  for (const item of items) {
    if (!item) continue;
    if (/(may contain|traces? of|allergen|produced in|factory|cross[- ]contamination)/i.test(item)) continue;

    const formatted = formatIngredientTitle(item);
    const key = formatted.toLowerCase();
    if (formatted && formatted.length > 1 && !seen.has(key)) {
      seen.add(key);
      cleanList.push(formatted);
    }
  }

  return cleanList;
}

/**
 * Fetch and parse barcode data from Open Food Facts API (free, open, no auth needed)
 */
export async function fetchProductByBarcode(barcode: string): Promise<{
  name: string;
  brand?: string;
  ingredients: string[];
  imageUrl?: string;
} | null> {
  const cleanBarcode = barcode.replace(/[\s\-]+/g, '').trim();
  if (!cleanBarcode || !/^[\dA-Za-z]{6,16}$/.test(cleanBarcode)) {
    return null;
  }

  // Codes to attempt: original, leading-zero padded (if 12-digit UPC), or leading-zero stripped (if 13-digit EAN starting with 0)
  const candidateCodes = [cleanBarcode];
  if (/^\d{12}$/.test(cleanBarcode)) {
    candidateCodes.push(`0${cleanBarcode}`);
  } else if (/^0\d{12}$/.test(cleanBarcode)) {
    candidateCodes.push(cleanBarcode.slice(1));
  }

  const fields =
    'product_name,product_name_en,generic_name,brands,ingredients_text_en,ingredients_text,ingredients_tags,ingredients,image_url,image_front_small_url';

  for (const code of candidateCodes) {
    try {
      const res = await fetch(
        `https://world.openfoodfacts.org/api/v2/product/${encodeURIComponent(code)}.json?fields=${fields}`,
        {
          headers: {
            'User-Agent': 'DigestLog - Digestive Health App - Web',
          },
        }
      );

      if (!res.ok) continue;

      const data = await res.json();
      if (data.status !== 1 || !data.product) {
        continue;
      }

      const prod = data.product;
      const name = prod.product_name || prod.product_name_en || prod.generic_name || 'Scanned Food Product';
      const brand = prod.brands || '';

      // Extract ingredients from text or tags
      const ingredientsText = prod.ingredients_text_en || prod.ingredients_text || '';
      let ingredients = cleanProductIngredientsText(ingredientsText);

      // Fallback to tags or structured array if text was empty or had no parsed items
      if (ingredients.length === 0) {
        const rawTags: string[] = Array.isArray(prod.ingredients_tags) ? prod.ingredients_tags : [];
        if (rawTags.length > 0) {
          ingredients = cleanIngredientsArray(rawTags);
        } else if (Array.isArray(prod.ingredients) && prod.ingredients.length > 0) {
          const rawItems = prod.ingredients.map((item: any) => item.text || item.id || '');
          ingredients = cleanIngredientsArray(rawItems);
        }
      }

      const imageUrl = prod.image_url || prod.image_front_small_url || undefined;

      // AI Fallback if barcode found product name but no ingredients
      if (ingredients.length === 0 && name) {
        ingredients = await inferIngredientsFromProductName(name, brand);
      }

      return {
        name: brand ? `${brand} ${name}` : name,
        brand,
        ingredients,
        imageUrl,
      };
    } catch (err) {
      console.warn(`Open Food Facts API error on code ${code}:`, err);
    }
  }

  return null;
}

export interface OpenFoodFactsProductResult {
  code: string;
  name: string;
  brand?: string;
  ingredients: string[];
  imageUrl?: string;
  ingredientsText?: string;
}

const OFF_SEARCH_CACHE = new Map<string, OpenFoodFactsProductResult[]>();

/**
 * Search the Open Food Facts database for branded commercial food products and ingredients.
 * Uses high-speed Elasticsearch cluster, Cloudflare Worker proxy, and fuzzy query relaxation.
 */
export async function searchOpenFoodFacts(
  query: string,
  limit = 8
): Promise<OpenFoodFactsProductResult[]> {
  const cleanQuery = query.trim();
  if (!cleanQuery || cleanQuery.length < 2) return [];

  const cacheKey = `${cleanQuery.toLowerCase()}_${limit}`;
  if (OFF_SEARCH_CACHE.has(cacheKey)) {
    return OFF_SEARCH_CACHE.get(cacheKey)!;
  }

  let results: OpenFoodFactsProductResult[] = [];

  // Strategy 1: Call /api/search-products Cloudflare Worker proxy (Edge cached & CORS safe)
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 4000);
    const proxyRes = await fetch(`/api/search-products?q=${encodeURIComponent(cleanQuery)}&limit=${limit}`, {
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (proxyRes.ok) {
      const data = await proxyRes.json();
      if (Array.isArray(data.products) && data.products.length > 0) {
        results = data.products;
      }
    }
  } catch {
    // Fall back to direct search below
  }

  // Strategy 2: Direct query to search.openfoodfacts.org Elasticsearch cluster
  if (results.length === 0) {
    try {
      const fields =
        'code,product_name,product_name_en,generic_name,brands,ingredients_text,ingredients_text_en,ingredients_tags,ingredients,image_front_small_url,image_url';
      const url = `https://search.openfoodfacts.org/search?q=${encodeURIComponent(
        cleanQuery
      )}&page_size=${limit}&fields=${fields}`;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 4500);

      const res = await fetch(url, {
        headers: {
          'User-Agent': 'DigestLog - Digestive Health App - Web',
        },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (res.ok) {
        const data = await res.json();
        const items: any[] = data.hits || data.products || [];

        for (const prod of items) {
          const rawName = prod.product_name || prod.product_name_en || prod.generic_name;
          if (!rawName || typeof rawName !== 'string' || !rawName.trim()) continue;

          let brand = '';
          if (Array.isArray(prod.brands) && prod.brands.length > 0) {
            brand = String(prod.brands[0] || '').trim();
          } else if (typeof prod.brands === 'string') {
            brand = prod.brands.split(',')[0].trim();
          }

          const fullName =
            brand && !rawName.toLowerCase().includes(brand.toLowerCase())
              ? `${brand} ${rawName}`
              : rawName;

          const ingredientsText = prod.ingredients_text_en || prod.ingredients_text || '';
          let ingredients = cleanProductIngredientsText(ingredientsText);

          if (ingredients.length === 0) {
            const rawTags: string[] = Array.isArray(prod.ingredients_tags) ? prod.ingredients_tags : [];
            if (rawTags.length > 0) {
              ingredients = cleanIngredientsArray(rawTags);
            } else if (Array.isArray(prod.ingredients) && prod.ingredients.length > 0) {
              const rawItems = prod.ingredients.map((item: any) => item.text || item.id || '');
              ingredients = cleanIngredientsArray(rawItems);
            }
          }

          if (ingredients.length === 0) {
            const decomp = decomposeCompositeDish(rawName);
            if (decomp && decomp.ingredients.length > 0) {
              ingredients = decomp.ingredients;
            } else {
              const can = canonicalizeIngredient(rawName);
              if (can) ingredients = [can];
            }
          }

          const imageUrl = prod.image_front_small_url || prod.image_url || undefined;

          results.push({
            code: prod.code || '',
            name: fullName,
            brand: brand || undefined,
            ingredients,
            imageUrl,
            ingredientsText: ingredientsText || undefined,
          });
        }
      }
    } catch (err) {
      console.warn('Direct Open Food Facts Search error:', err);
    }
  }

  // Strategy 3: Query relaxation if 0 results found on a multi-word search (e.g. "Tesco whole milk blue" -> "whole milk")
  if (results.length === 0 && cleanQuery.includes(' ')) {
    const words = cleanQuery.split(/\s+/).filter((w) => w.length > 2);
    if (words.length >= 2) {
      const relaxedQuery = words.slice(-2).join(' ');
      if (relaxedQuery !== cleanQuery) {
        try {
          const relaxedResults = await searchOpenFoodFacts(relaxedQuery, limit);
          if (relaxedResults.length > 0) {
            results = relaxedResults;
          }
        } catch {}
      }
    }
  }

  if (OFF_SEARCH_CACHE.size > 200) {
    OFF_SEARCH_CACHE.clear();
  }
  if (results.length > 0) {
    OFF_SEARCH_CACHE.set(cacheKey, results);
  }

  return results;
}

/**
 * Infer canonical ingredients for a food product by name using Gemini AI & composite recipe decomposition
 */
export async function inferIngredientsFromProductName(name: string, brand?: string): Promise<string[]> {
  const query = (brand ? `${brand} ${name}` : name).trim();
  if (!query) return [];

  // 1. Check composite recipes
  const decomp = decomposeCompositeDish(query);
  if (decomp && decomp.ingredients.length > 0) {
    return decomp.ingredients;
  }

  // 2. Call Direct Gemini / API describe endpoint
  try {
    const result = await describeMealDirectOrProxy(query);
    if (result.success && result.data && Array.isArray(result.data.ingredients) && result.data.ingredients.length > 0) {
      const rawTags = result.data.ingredients.map((i: any) => (typeof i === 'string' ? i : (i.name || i.title || '')));
      return cleanIngredientsArray(rawTags);
    }
  } catch (e) {
    console.warn('API describe fallback error:', e);
  }

  // 3. Word-based fallback
  const words = query
    .split(/,|\band\b|\bwith\b|\b&\b|\+/i)
    .map((s) => s.trim())
    .filter((s) => s.length >= 2 && s.length <= 30)
    .map((s) => canonicalizeIngredient(s));
  return cleanIngredientsArray(words);
}

/**
 * Normalizes and cleans a single meal log:
 * 1. Automatically identifies "gluten-free", "dairy-free", etc. and moves them to anti_aggravates.
 * 2. Strips punctuation artifacts (e.g. "gluten.", "wheat flour,")
 * 3. Removes duplicate/redundant ingredients.
 * 4. Ensures false trigger tags (like "gluten" on a gluten-free meal) are eliminated.
 */
export function cleanAndNormalizeMeal(meal: Meal): Meal {
  const antiAggravatesSet = new Set<string>();
  if (meal.anti_aggravates) {
    meal.anti_aggravates
      .split(',')
      .map((a) => a.trim())
      .filter(Boolean)
      .forEach((a) => antiAggravatesSet.add(a.charAt(0).toUpperCase() + a.slice(1).toLowerCase()));
  }

  // Scan description and tags for explicit free-from indicators
  const combinedText = `${meal.description || ''} ${meal.ingredients_tags || ''}`.toLowerCase();
  if (/gluten[- ]free|wheat[- ]free|\bgf\b|without gluten|no gluten/i.test(combinedText)) {
    antiAggravatesSet.add('Gluten');
  }
  if (/dairy[- ]free|lactose[- ]free|non[- ]dairy|without dairy|no dairy/i.test(combinedText)) {
    antiAggravatesSet.add('Dairy');
  }
  if (/allium[- ]free|garlic[- ]free|onion[- ]free|without garlic|no garlic|no onion/i.test(combinedText)) {
    antiAggravatesSet.add('Allium');
  }
  if (/nut[- ]free|peanut[- ]free/i.test(combinedText)) {
    antiAggravatesSet.add('Nuts');
  }
  if (/egg[- ]free/i.test(combinedText)) {
    antiAggravatesSet.add('Eggs');
  }
  if (/soy[- ]free|soya[- ]free/i.test(combinedText)) {
    antiAggravatesSet.add('Soy');
  }

  // Clean raw tags
  const cleanedTags: string[] = [];
  const seenCanonicalKeys = new Set<string>();

  if (meal.ingredients_tags) {
    const rawTags = meal.ingredients_tags
      .split(/[,;\n]/)
      .map((t) => t.replace(/^[^\w]+|[^\w]+$/g, '').trim()) // strip trailing/leading punctuation
      .filter(Boolean);

    for (const tag of rawTags) {
      const lower = tag.toLowerCase().trim();

      // Skip standalone negative qualifiers from positive ingredient list
      if (/^(gluten[- ]free|dairy[- ]free|lactose[- ]free|wheat[- ]free|nut[- ]free|egg[- ]free|soy[- ]free)$/i.test(lower)) {
        continue;
      }

      // If meal is confirmed gluten-free and tag is literally "gluten" or "wheat", skip it
      if (antiAggravatesSet.has('Gluten') && (lower === 'gluten' || lower === 'wheat' || lower === 'wheat flour')) {
        continue;
      }

      // If meal is confirmed dairy-free and tag is literally "dairy" or "lactose" or "milk", skip it
      if (antiAggravatesSet.has('Dairy') && (lower === 'dairy' || lower === 'lactose' || lower === 'milk')) {
        continue;
      }

      const canonical = canonicalizeIngredient(tag);
      const key = canonical.toLowerCase();
      if (canonical && canonical.length >= 2 && !seenCanonicalKeys.has(key)) {
        seenCanonicalKeys.add(key);
        cleanedTags.push(canonical);
      }
    }
  }

  const newAntiAggravates = Array.from(antiAggravatesSet).join(', ');
  const newIngredientsTags = cleanedTags.join(', ');

  return {
    ...meal,
    ingredients_tags: newIngredientsTags,
    anti_aggravates: newAntiAggravates || undefined,
  };
}

/**
 * Migration function to batch clean all historical meals across storage
 */
export function migrateAndCleanHistoricalMeals(meals: Meal[]): { cleaned: Meal[]; hasChanges: boolean } {
  let hasChanges = false;
  const cleaned = meals.map((meal) => {
    const updated = cleanAndNormalizeMeal(meal);
    if (
      updated.ingredients_tags !== meal.ingredients_tags ||
      updated.anti_aggravates !== meal.anti_aggravates
    ) {
      hasChanges = true;
    }
    return updated;
  });

  return { cleaned, hasChanges };
}

/**
 * Detect recipe yield and portions eaten from natural language text
 * Examples:
 * - "Recipe for 4 people, I ate 1 bowl" -> { recipeServings: 4, portionsEaten: 1, multiplier: 0.25 }
 * - "Serves 6, had 2 portions" -> { recipeServings: 6, portionsEaten: 2, multiplier: 0.333 }
 * - "Makes 8 slices, ate 1" -> { recipeServings: 8, portionsEaten: 1, multiplier: 0.125 }
 * - "Millionaire shortbread serves 16, ate 1" -> { recipeServings: 16, portionsEaten: 1, multiplier: 0.0625 }
 */
export function extractRecipeServingsAndPortion(text: string): {
  recipeServings?: number;
  portionsEaten?: number;
  multiplier?: number;
} {
  if (!text) return {};
  const lower = text.toLowerCase();

  // Check explicit fraction pattern: e.g. "1/4 of", "1/2 of", "1/3 of"
  const fractionMatch = lower.match(/(\d+)\s*\/\s*(\d+)\s*(?:of|portion|serving)/i);
  if (fractionMatch && fractionMatch[1] && fractionMatch[2]) {
    const num = parseFloat(fractionMatch[1]);
    const den = parseFloat(fractionMatch[2]);
    if (num > 0 && den > 0) {
      return {
        recipeServings: den,
        portionsEaten: num,
        multiplier: Math.round((num / den) * 1000) / 1000,
      };
    }
  }

  // Match pattern: serves / for / makes X (people / servings / portions / slices / pieces / bowls)
  let recipeServings: number | undefined;
  const yieldMatch = lower.match(/(?:serves?|for|makes?|yields?|batch of|recipe of|recipe for)\s*(\d+(?:\.\d+)?)\s*(?:people|persons?|servings?|portions?|slices?|pieces?|bowls?|plates?|cups?)?/i);
  if (yieldMatch && yieldMatch[1]) {
    const num = parseFloat(yieldMatch[1]);
    if (!isNaN(num) && num > 0) {
      recipeServings = num;
    }
  }

  // Match pattern: ate / had / eaten / consumed / having Y (portions / servings / slices / pieces / bowls / of / out of)
  let portionsEaten: number | undefined;
  const eatenMatch = lower.match(/(?:i\s*(?:ate|had|consumed|am having)|ate|had|eaten|eats?|having)\s*(\d+(?:\.\d+)?|\bhalf\b|\ba quarter\b|\bone\b|\btwo\b|\bthree\b|\bfour\b)?\s*(?:of it|portions?|servings?|slices?|pieces?|bowls?|plates?|cups?)?/i);
  if (eatenMatch && eatenMatch[1]) {
    const rawVal = eatenMatch[1].toLowerCase();
    if (rawVal === 'half') portionsEaten = 0.5;
    else if (rawVal === 'a quarter') portionsEaten = 0.25;
    else if (rawVal === 'one') portionsEaten = 1;
    else if (rawVal === 'two') portionsEaten = 2;
    else if (rawVal === 'three') portionsEaten = 3;
    else if (rawVal === 'four') portionsEaten = 4;
    else {
      const num = parseFloat(rawVal);
      if (!isNaN(num) && num > 0) {
        portionsEaten = num;
      }
    }
  }

  if (recipeServings !== undefined) {
    const eaten = portionsEaten !== undefined ? portionsEaten : 1;
    const mult = Math.round((eaten / recipeServings) * 1000) / 1000;
    return {
      recipeServings,
      portionsEaten: eaten,
      multiplier: mult,
    };
  }

  return {};
}

export function formatServingMultiplierLabel(
  multiplier: number = 1.0,
  recipeServings?: number,
  portionsEaten?: number
): string {
  if (recipeServings && recipeServings > 1) {
    const eaten = portionsEaten !== undefined ? portionsEaten : Math.round(multiplier * recipeServings * 10) / 10;
    return `${eaten} of ${recipeServings} serv (${multiplier}x)`;
  }
  if (multiplier === 1.0) return '1.0x (1 serv)';
  return `${multiplier}x`;
}

/**
 * Intelligent Meal & Dish Title Synthesizer
 * Automatically generates clean, natural meal and dish names from ingredients
 * (e.g. Espresso -> "Espresso", Espresso + Oat Milk -> "Oat Milk Latte", Eggs + Toast -> "Eggs on Toast")
 */
export function synthesizeDishTitle(
  ingredients: (string | { name: string; canonicalName?: string })[]
): string {
  if (!ingredients || ingredients.length === 0) return '';

  const cleanNames = ingredients
    .map((item) => {
      if (typeof item === 'string') return item.trim();
      return (item.name || item.canonicalName || '').trim();
    })
    .filter(Boolean)
    .map((n) => formatIngredientTitle(n));

  if (cleanNames.length === 0) return '';

  const lowerList = cleanNames.map((n) => n.toLowerCase());

  // Helper check
  const hasItem = (regex: RegExp) => lowerList.some((n) => regex.test(n));
  const getItem = (regex: RegExp) => cleanNames.find((n) => regex.test(n.toLowerCase()));

  // 1. Single Ingredient
  if (cleanNames.length === 1) {
    const single = cleanNames[0];
    const sLower = single.toLowerCase();
    if (/^espresso\b/i.test(sLower)) return 'Espresso';
    if (/^black coffee\b|filter coffee|brewed coffee|americano/i.test(sLower)) return 'Coffee';
    if (/^matcha\b/i.test(sLower)) return 'Matcha Green Tea';
    return single;
  }

  // 2. Beverage & Coffee Heuristics
  const isEspresso = hasItem(/\bespresso\b|\bristretto\b/i);
  const isCoffee = isEspresso || hasItem(/\bcoffee\b|\bcafe\b|\bamericano\b/i);
  const isTea = hasItem(/\btea\b|\bchai\b|\bearl grey\b|\bmatcha\b/i);
  const isProteinPowder = hasItem(/protein powder|whey|casein|pea protein/i);

  const milkItem = getItem(/oat milk|almond milk|soy milk|coconut milk|cow milk|whole milk|skim milk|semi-skimmed milk|lactose-free milk|milk|cream|creamer/i);
  const sweetItem = getItem(/sugar|honey|maple syrup|syrup|vanilla|caramel|stevia/i);

  if (isCoffee) {
    const milkName = milkItem ? milkItem.toLowerCase() : '';
    let milkPrefix = '';
    if (/oat/i.test(milkName)) milkPrefix = 'Oat Milk ';
    else if (/almond/i.test(milkName)) milkPrefix = 'Almond Milk ';
    else if (/soy/i.test(milkName)) milkPrefix = 'Soy Milk ';
    else if (/coconut/i.test(milkName)) milkPrefix = 'Coconut Milk ';

    if (isEspresso && milkItem) {
      return `${milkPrefix}Latte`.trim();
    }
    if (milkItem) {
      return `${milkPrefix}Coffee`.trim();
    }
    if (sweetItem && cleanNames.length === 2) {
      return 'Sweetened Coffee';
    }
    if (cleanNames.length <= 3 && !hasItem(/chicken|beef|egg|rice|pasta|bread/i)) {
      return isEspresso ? 'Espresso' : 'Coffee';
    }
  }

  if (isTea) {
    if (hasItem(/matcha/i) && milkItem) return 'Matcha Latte';
    if (hasItem(/chai/i) && milkItem) return 'Chai Latte';
    if (milkItem) return 'Tea with Milk';
    const teaName = getItem(/\btea\b|\bmatcha\b|\bchai\b/i) || 'Tea';
    return teaName;
  }

  if (isProteinPowder) {
    if (hasItem(/banana|berry|berries|strawberry|blueberry/i)) {
      return 'Protein Smoothie';
    }
    return 'Protein Shake';
  }

  // 3. Breakfast & Morning Combos
  const isEgg = hasItem(/\begg\b|\beggs\b|scrambled egg|fried egg|poached egg|boiled egg|omelet|omelette/i);
  const isToast = hasItem(/\btoast\b|sourdough|bread|bagel|english muffin/i);
  const isBacon = hasItem(/\bbacon\b|pancetta|prosciutto|sausage|hash brown/i);
  const isOats = hasItem(/\boats\b|oatmeal|porridge|granola|muesli/i);
  const isYogurt = hasItem(/\byogurt\b|yoghurt|kefir|skyr/i);

  if (isEgg && isToast) {
    return 'Eggs on Toast';
  }
  if (isEgg && isBacon && cleanNames.length <= 4) {
    return 'Cooked Breakfast';
  }
  if (isOats) {
    const fruit = getItem(/berry|berries|blueberry|blueberries|strawberry|strawberries|banana|apple/i);
    const seedOrNut = getItem(/chia|flax|almond|walnut|pecan|peanut butter/i);
    if (fruit && seedOrNut) return `Oatmeal with ${fruit} & ${seedOrNut}`;
    if (fruit) return `Oatmeal with ${fruit}`;
    return 'Oatmeal Bowl';
  }
  if (isYogurt) {
    const fruit = getItem(/berry|berries|blueberry|blueberries|strawberry|strawberries|banana|apple|granola/i);
    if (fruit) return `Yogurt with ${fruit}`;
    return 'Yogurt Bowl';
  }

  // 4. Salads
  const isSaladGreen = hasItem(/lettuce|spinach|kale|arugula|rocket|mixed greens|salad/i);
  const proteinItem = getItem(/chicken|salmon|tuna|beef|steak|turkey|tofu|prawn|shrimp/i);
  if (isSaladGreen && proteinItem && cleanNames.length >= 3) {
    return `${proteinItem} Salad`;
  }

  // 5. Entrees: Protein + Grain/Starch + Veg
  const baseStarch = getItem(/rice|quinoa|pasta|spaghetti|noodle|noodles|potato|potatoes|sweet potato|mash/i);
  const keyVeg = getItem(/broccoli|asparagus|spinach|carrot|green beans|peppers|bell pepper|courgette|zucchini|mushrooms/i);

  if (proteinItem && baseStarch) {
    if (keyVeg && cleanNames.length >= 3) {
      return `${proteinItem} & ${baseStarch} with ${keyVeg}`;
    }
    return `${proteinItem} & ${baseStarch}`;
  }

  if (proteinItem && keyVeg && cleanNames.length <= 4) {
    return `${proteinItem} with ${keyVeg}`;
  }

  if (baseStarch && keyVeg && cleanNames.length <= 4) {
    return `${baseStarch} with ${keyVeg}`;
  }

  // 6. Generic Clean Pairings
  if (cleanNames.length === 2) {
    // If one is butter, salt, oil, or condiment, prioritize primary item
    if (hasItem(/butter|oil|salt|pepper|seasoning|mayo|dressing/i)) {
      const main = cleanNames.find((n) => !/butter|oil|salt|pepper|seasoning|mayo|dressing/i.test(n.toLowerCase()));
      if (main) return main;
    }
    return `${cleanNames[0]} & ${cleanNames[1]}`;
  }

  // 7. General Fallback for 3+ items
  // Filter out minor seasonings for title clarity
  const dominantItems = cleanNames.filter(
    (n) => !/salt|black pepper|pepper|oil|butter|seasoning|water|garlic powder|onion powder/i.test(n.toLowerCase())
  );

  if (dominantItems.length === 1) return dominantItems[0];
  if (dominantItems.length === 2) return `${dominantItems[0]} & ${dominantItems[1]}`;
  if (dominantItems.length >= 3) {
    return `${dominantItems[0]} with ${dominantItems[1]} & ${dominantItems[2]}`;
  }

  return `${cleanNames[0]} & ${cleanNames[1]}`;
}

/**
 * Synthesizes overall meal description from multi-dish plates
 */
export function synthesizeMealDescription(dishes: { title?: string; isCustomTitle?: boolean; ingredients?: any[] }[]): string {
  if (!dishes || dishes.length === 0) return 'Meal';

  const validTitles = dishes
    .map((d) => {
      if (d.title && d.title !== 'MAIN DISH' && d.title !== 'DISH' && d.title !== 'MEAL' && d.title !== 'FOOD 1' && d.title !== 'FOOD 2') {
        return formatIngredientTitle(d.title);
      }
      if (d.ingredients && d.ingredients.length > 0) {
        return synthesizeDishTitle(d.ingredients);
      }
      return '';
    })
  if (validTitles.length === 0) return 'Meal';
  if (validTitles.length === 1) return validTitles[0];
  if (validTitles.length === 2) return `${validTitles[0]} & ${validTitles[1]}`;
  return `${validTitles[0]}, ${validTitles[1]} & ${validTitles[2]}`;
}

/**
 * Shared Normalization Engine for AI Parsed Ingredients (Vision + Describe)
 * 
 * Standardizes raw AI ingredient objects/strings across Meal Logger & FODMAP Checker:
 * 1. Matches against 1,670 Monash Foods database first.
 * 2. Uses getRealisticCulinaryServing for standard gram weights.
 * 3. Formats clean title case names.
 * 4. Attaches Monash traffic light ratings, culprits, safe limits, and nutrition macros.
 * 5. Automatically strips non-nutritive seasonings (water, salt, pepper).
 */
export function normalizeParsedAiIngredients(rawIngredients: any[]): MealIngredientItem[] {
  if (!Array.isArray(rawIngredients) || rawIngredients.length === 0) return [];

  const converted = rawIngredients
    .map((item: any, idx: number): MealIngredientItem => {
      if (typeof item === 'string') {
        const rawName = item.trim();
        const ev = matchIngredientToFodmap(rawName);
        const matched = ev.matchedFood;
        const canonical = matched ? matched.name : (canonicalizeIngredient(rawName) || rawName);
        const realistic = getRealisticCulinaryServing(rawName, matched);
        const cleanName = formatIngredientTitle(rawName) || rawName;

        return {
          id: `ing-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 6)}`,
          name: cleanName,
          canonicalName: canonical || cleanName,
          amount: realistic.amount || 1,
          unit: realistic.unit || 'g',
          gramsEstimate: realistic.grams || 50,
          rating: ev.rating,
          primaryCulprit: ev.primaryCulprit,
          servingLimitNote: matched?.servings?.low?.title
            ? `Safe limit: ${matched.servings.low.title}`
            : (ev.servingLimitNote || realistic.servingLimitNote),
          fodmaps: matched?.fodmaps,
          isCertifiedSwap: ev.rating === 'low',
        };
      }

      const rawName = (item.name || item.title || 'Ingredient').trim();
      const ev = matchIngredientToFodmap(rawName);
      const matched = ev.matchedFood;
      const canonical = matched ? matched.name : (canonicalizeIngredient(rawName) || rawName);
      const realistic = getRealisticCulinaryServing(rawName, matched);

      const rawAmount = Number(item.amount);
      const rawUnit = (item.unit || '').trim().toLowerCase();
      const rawGrams = Number(item.grams);

      const isCountUnit =
        !rawUnit ||
        /^(banana|bananas|egg|eggs|slice|slices|apple|apples|rasher|rashers|piece|pieces|stick|sticks|carrot|carrots|tomato|tomatoes|sausage|sausages|potato|potatoes|sachet|sachets|serving|servings|item|items|count|whole|half)$/i.test(
          rawUnit
        ) ||
        rawUnit === canonical.toLowerCase() ||
        rawUnit + 's' === canonical.toLowerCase();

      let finalAmount = rawAmount;
      let finalUnit = rawUnit || realistic.unit || 'g';
      if (rawUnit === 'teaspoon' || rawUnit === 'teaspoons') finalUnit = 'tsp';
      else if (rawUnit === 'tablespoon' || rawUnit === 'tablespoons') finalUnit = 'tbsp';
      let finalGrams = rawGrams;

      // 1. Explicit gram/ml measurement specified
      if (rawUnit === 'g' || rawUnit === 'gram' || rawUnit === 'grams' || rawUnit === 'ml') {
        finalAmount = rawAmount > 0 ? rawAmount : (rawGrams > 0 ? rawGrams : realistic.grams || 50);
        finalUnit = rawUnit === 'ml' ? 'ml' : 'g';
        finalGrams = finalAmount;
      }
      // 2. Count unit with large gram weight in amount (e.g. amount: 300, unit: "banana")
      else if (isCountUnit && rawAmount > 10) {
        finalGrams = rawAmount;
        const unitWeight = realistic.grams || 100;
        finalAmount = Math.max(1, Math.round((finalGrams / unitWeight) * 10) / 10);
        finalUnit = realistic.unit || rawUnit || 'piece';
      }
      // 3. No amount provided (fallback to realistic portion)
      else if (!rawAmount || isNaN(rawAmount) || rawAmount <= 0) {
        finalAmount = realistic.amount || 1;
        finalUnit = finalUnit || realistic.unit || 'g';
        finalGrams = rawGrams && rawGrams > 0 ? rawGrams : realistic.grams || 50;
      }
      // 4. Standard specified unit (e.g. 2 tbsp, 1 tsp, 1 slice, 1 cup)
      else {
        finalAmount = rawAmount;
        if (!rawGrams || isNaN(rawGrams) || rawGrams === 100) {
          const perUnitGrams =
            finalUnit === 'tsp'
              ? 5
              : finalUnit === 'tbsp'
              ? 15
              : realistic.grams && realistic.amount
              ? realistic.grams / realistic.amount
              : 50;
          finalGrams = Math.round(finalAmount * perUnitGrams);
        }
      }

      const safeNote = matched?.servings?.low?.title
        ? `Safe limit: ${matched.servings.low.title}`
        : (ev.servingLimitNote || realistic.servingLimitNote);

      const cleanName = formatIngredientTitle(rawName) || rawName;

      return {
        id: `ing-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 6)}`,
        name: cleanName,
        canonicalName: canonical || cleanName,
        amount: finalAmount,
        unit: finalUnit,
        gramsEstimate: finalGrams,
        rating: ev.rating,
        primaryCulprit: ev.primaryCulprit,
        servingLimitNote: safeNote,
        fodmaps: matched?.fodmaps,
        isCertifiedSwap: ev.rating === 'low',
        calories: item.calories,
        protein: item.protein,
        fat: item.fat,
        carbs: item.carbs,
      };
    })
    .filter((it) => !/^(water|tap water|salt|table salt|sea salt|black pepper|pepper|cooking spray)$/i.test(it.name));

  return converted;
}



